import { Injectable, signal } from '@angular/core';
import {
  DEV_INJECT_PROFESSION_SESSION_KEY,
  readEligibilityLast4DevInject,
  writeEligibilityLast4DevInject,
} from '@brightside-web/desktop/data-access/shared';
import {
  DEV_SMS_PREFERENCES_MODE_SESSION_KEY,
  type SmsPreferencesDevMode,
} from '@brightside-web/desktop-v3/feature/preferences';
import {
  CardCurrentState,
  type CoreAccountsInterface,
  type DashboardRecentTransactionsState,
} from '@brightside-web/desktop/feature/financial-accounts-core';

import {
  dashboardRecentTransactionsDevState,
  type DashboardRecentTransactionsDevMode,
} from './dashboard-dev-mock.data';
import {
  createDevMockExternalTransferAccount,
  DEV_MOCK_EXTERNAL_TRANSFER_ACCOUNT_ID_BASE,
} from './transfer-funds-dev-mock.data';

export type { DashboardRecentTransactionsDevMode } from './dashboard-dev-mock.data';
export { DASHBOARD_RECENT_TRANSACTIONS_DEV_OPTIONS } from './dashboard-dev-mock.data';
export type { SmsPreferencesDevMode } from '@brightside-web/desktop-v3/feature/preferences';

/** Dev override for spending card: null = use API, 'NONE' = no card, else a CardCurrentState. */
export type SimulatedCardState = 'NONE' | CardCurrentState | null;

@Injectable({
  providedIn: 'root'
})
export class DevMenuService {
  toggleDisplayCreditActionCard = signal<boolean|undefined>(undefined);

  /** When true, the next getDocvTransactionToken() will receive a bad token (for testing invalid-token flow). */
  private _useBadSocureTokenOnNextAttempt = signal(false);

  readonly useBadSocureTokenOnNextAttempt = this._useBadSocureTokenOnNextAttempt.asReadonly();

  /** Override card state for spending dashboard. null = use real API data. */
  private _simulatedCardState = signal<SimulatedCardState>(null);
  readonly simulatedCardState = this._simulatedCardState.asReadonly();

  /** Kept for CardOrderMockInterceptor. Prefer simulatedCardState() === 'NONE'. */
  simulateNoCard(): boolean {
    return this._simulatedCardState() === 'NONE';
  }

  /** Kept for CardOrderMockInterceptor. Prefer simulatedCardState() === DIGITAL_ACTIVE. */
  simulateIsDigitalOnly(): boolean {
    return this._simulatedCardState() === CardCurrentState.DIGITAL_ACTIVE;
  }

  setSimulatedCardState(state: SimulatedCardState): void {
    this._simulatedCardState.set(state);
  }

  /** Override recent-transactions panel on savings/spending dashboards. null = use API. */
  private _dashboardRecentTransactionsOverride = signal<DashboardRecentTransactionsDevMode | null>(
    null
  );
  readonly dashboardRecentTransactionsOverride =
    this._dashboardRecentTransactionsOverride.asReadonly();

  setDashboardRecentTransactionsOverride(mode: DashboardRecentTransactionsDevMode | null): void {
    this._dashboardRecentTransactionsOverride.set(mode);
  }

  /** Applies dev-menu recent-transactions override when set; otherwise returns API state. */
  resolveDashboardRecentTransactionsState(
    api: DashboardRecentTransactionsState
  ): DashboardRecentTransactionsState {
    const mode = this._dashboardRecentTransactionsOverride();
    if (mode === null) {
      return api;
    }
    return dashboardRecentTransactionsDevState(mode);
  }

  toggleCreditActionCard() {
    this.toggleDisplayCreditActionCard.set(!this.toggleDisplayCreditActionCard());
  }

  toggleBadSocureTokenOnNextAttempt(): void {
    this._useBadSocureTokenOnNextAttempt.set(!this._useBadSocureTokenOnNextAttempt());
  }

  consumeBadSocureTokenIfRequested(): boolean {
    const requested = this._useBadSocureTokenOnNextAttempt();
    if (requested) {
      this._useBadSocureTokenOnNextAttempt.set(false);
      return true;
    }
    return false;
  }

  /**
   * When true, data-collect adds a synthetic `profession` field only when the API did not return one.
   * Mirrored to sessionStorage for ProductEligibilityService (no circular import).
   */
  private _professionCollectionInjectWhenMissing = signal(
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DEV_INJECT_PROFESSION_SESSION_KEY) === '1'
  );
  readonly professionCollectionInjectWhenMissing = this._professionCollectionInjectWhenMissing.asReadonly();

  toggleProfessionCollectionInjectWhenMissing(): void {
    this._professionCollectionInjectWhenMissing.update((v) => !v);
    this._syncProfessionInjectSession();
  }

  private _syncProfessionInjectSession(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(
      DEV_INJECT_PROFESSION_SESSION_KEY,
      this._professionCollectionInjectWhenMissing() ? '1' : '0'
    );
  }

  /**
   * When true, eligibility data-collect forces `last_four_ssn` to `value: ""` (dev menu).
   * Persisted in localStorage so it survives Auth0 redirect when enabled before sign-in.
   */
  private _eligibilityLast4Inject = signal(readEligibilityLast4DevInject());
  readonly eligibilityLast4Inject = this._eligibilityLast4Inject.asReadonly();

  toggleEligibilityLast4Inject(): void {
    this._eligibilityLast4Inject.update((v) => !v);
    writeEligibilityLast4DevInject(this._eligibilityLast4Inject());
  }

  /** Dev-injected external accounts appended to GET /accounts/transfer/create (non-prod). */
  private readonly _mockExternalTransferAccounts = signal<CoreAccountsInterface[]>([]);
  readonly mockExternalTransferAccounts = this._mockExternalTransferAccounts.asReadonly();

  /** Bumped when mock external transfer accounts are added or cleared. */
  private readonly _transferFundsMockAccountsRevision = signal(0);
  readonly transferFundsMockAccountsRevision = this._transferFundsMockAccountsRevision.asReadonly();

  addMockExternalTransferAccount(): void {
    this._appendMockExternalTransferAccount();
  }

  /** Appends a mock external account with serviceability.mustRepair for relink-modal testing. */
  addMockExternalTransferAccountNeedsRelink(): void {
    this._appendMockExternalTransferAccount({ needsRelink: true });
  }

  private _appendMockExternalTransferAccount(
    options: { needsRelink?: boolean } = {}
  ): void {
    const list = this._mockExternalTransferAccounts();
    const index = list.length;
    const accountId = DEV_MOCK_EXTERNAL_TRANSFER_ACCOUNT_ID_BASE + index;
    this._mockExternalTransferAccounts.set([
      ...list,
      createDevMockExternalTransferAccount(accountId, index, options),
    ]);
    this._transferFundsMockAccountsRevision.update((revision) => revision + 1);
  }

  clearMockExternalTransferAccounts(): void {
    this._mockExternalTransferAccounts.set([]);
    this._transferFundsMockAccountsRevision.update((revision) => revision + 1);
  }

  /** Dev override for SMS preferences API (non-prod). Mirrored to sessionStorage for ClientPreferencesService. */
  private _smsPreferencesDevMode = signal<SmsPreferencesDevMode>(
    DevMenuService._readSmsPreferencesDevModeFromSession(),
  );
  readonly smsPreferencesDevMode = this._smsPreferencesDevMode.asReadonly();

  setSmsPreferencesDevMode(mode: SmsPreferencesDevMode): void {
    this._smsPreferencesDevMode.set(mode);
    this._syncSmsPreferencesDevModeSession();
  }

  private static _readSmsPreferencesDevModeFromSession(): SmsPreferencesDevMode {
    if (typeof sessionStorage === 'undefined') {
      return 'off';
    }
    const raw = sessionStorage.getItem(DEV_SMS_PREFERENCES_MODE_SESSION_KEY);
    if (raw === 'force-prompt' || raw === 'get-fail' || raw === 'patch-fail') {
      return raw;
    }
    return 'off';
  }

  private _syncSmsPreferencesDevModeSession(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const mode = this._smsPreferencesDevMode();
    if (mode === 'off') {
      sessionStorage.removeItem(DEV_SMS_PREFERENCES_MODE_SESSION_KEY);
    } else {
      sessionStorage.setItem(DEV_SMS_PREFERENCES_MODE_SESSION_KEY, mode);
    }
  }
}
